import React from 'react';
import { X } from 'lucide-react';
import type { LeaseholderFilter } from '../../types/leaseholder';

interface LeaseholderFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  filters: LeaseholderFilter;
  onFilterChange: (filters: Partial<LeaseholderFilter>) => void;
}

export default function LeaseholderFilterPanel({
  isOpen,
  onClose,
  filters,
  onFilterChange
}: LeaseholderFilterPanelProps) {
  if (!isOpen) return null;

  // Get unique positions and states for filter dropdowns
  const positions = ['CEO', 'Director', 'Manager', 'Administrator'];
  const states = ['NT', 'NSW', 'QLD', 'VIC', 'SA', 'WA', 'TAS', 'ACT'];

  return (
    <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Filters</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Position
            </label>
            <select
              value={filters.position || ''}
              onChange={(e) => onFilterChange({ position: e.target.value || null })}
              className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">All Positions</option>
              {positions.map((position) => (
                <option key={position} value={position}>
                  {position}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State
            </label>
            <select
              value={filters.state || ''}
              onChange={(e) => onFilterChange({ state: e.target.value || null })}
              className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">All States</option>
              {states.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ABN Status
            </label>
            <select
              value={filters.hasABN === null ? '' : filters.hasABN ? 'yes' : 'no'}
              onChange={(e) => {
                const value = e.target.value;
                onFilterChange({ 
                  hasABN: value === '' ? null : value === 'yes'
                });
              }}
              className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">All</option>
              <option value="yes">Has ABN</option>
              <option value="no">No ABN</option>
            </select>
          </div>

          <div className="pt-4 border-t">
            <button
              onClick={() => onFilterChange({
                position: null,
                state: null,
                hasABN: null
              })}
              className="w-full px-4 py-2 text-sm text-primary hover:bg-primary/5 rounded-md"
            >
              Clear All Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
